import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  CheckCircle2,
  Search,
  ShieldAlert,
  UserRoundSearch,
} from 'lucide-react';
import { ROLES } from '../../config/accessControl';
import { VOTER_CLASSIFICATIONS } from '../../config/voterClassification';
import { electoralRegisterService } from '../../services';
import { voterService } from '../../services';
import Alert from '../common/Alert';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ClassificationStatus from '../common/ClassificationStatus';
import Modal from '../common/Modal';
import Checkbox from './Checkbox';
import Input from './Input';
import Select from './Select';

const classificationOptions = [
  { value: VOTER_CLASSIFICATIONS.BLACK, label: 'Verde · Genera gasto' },
  { value: VOTER_CLASSIFICATIONS.WHITE, label: 'Blanco · No genera gasto' },
];

function VoterRegistrationFlow({ open, onClose, onSuccess, user }) {
  const [step, setStep] = useState('lookup');
  const [document, setDocument] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [registerFound, setRegisterFound] = useState(false);
  const [padronResults, setPadronResults] = useState([]);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [existingVoter, setExistingVoter] = useState(null);
  const [options, setOptions] = useState({ coordinators: [], leaders: [] });
  const [selectedCoordinatorId, setSelectedCoordinatorId] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const selectedClassification = watch('classification', VOTER_CLASSIFICATIONS.WHITE);

  useEffect(() => {
    if (!open) return;
    setStep('lookup');
    setDocument('');
    setLookupError('');
    setRegisterFound(false);
    setPadronResults([]);
    setSelectedInscription(null);
    setExistingVoter(null);
    setSelectedCoordinatorId(user.relaciones?.coordinador_id || '');
    reset({});

    let mounted = true;
    voterService.getFilterOptions(user)
      .then((response) => mounted && setOptions(response.data))
      .catch(() => mounted && setOptions({ coordinators: [], leaders: [] }));
    return () => { mounted = false; };
  }, [open, reset, user]);

  const availableLeaders = useMemo(() =>
    options.leaders.filter((leader) =>
      !selectedCoordinatorId
      || String(leader.coordinatorId) === String(selectedCoordinatorId)
    ),
  [options.leaders, selectedCoordinatorId]);

  const searchRegister = async (event) => {
    event.preventDefault();
    const normalizedDocument = document.replace(/\D/g, '');
    if (normalizedDocument.length < 5 || normalizedDocument.length > 10) {
      setLookupError('Ingresa una cédula válida de 5 a 10 dígitos.');
      return;
    }

    setLookupLoading(true);
    setLookupError('');
    try {
      const [duplicateResponse, registerResponse] = await Promise.all([
        voterService.checkDuplicateDocument(normalizedDocument),
        electoralRegisterService.findByDocument(normalizedDocument),
      ]);
      const results = registerResponse.data.resultados || [];
      const found = registerResponse.data.encontrado && results.length > 0;
      const official = results[0] || null;
      const duplicate = duplicateResponse.data?.exists
        ? duplicateResponse.data.voter
        : null;
      setRegisterFound(found);
      setPadronResults(results);
      setSelectedInscription(official);
      setExistingVoter(duplicate);
      reset({
        cedula: normalizedDocument,
        nombres: official?.nombre || duplicate?.nombres || '',
        apellidos: official?.apellido || duplicate?.apellidos || '',
        telefono: duplicate?.telefono || '',
        direccion: '',
        barrio: '',
        referencia: '',
        ciudad: official?.distrito || duplicate?.ciudad || '',
        local_votacion: official?.local || duplicate?.local_votacion || '',
        mesa: official?.mesa || duplicate?.mesa || '',
        inscripcion_id: official?.id || null,
        puntero_id: user.relaciones?.puntero_id || '',
        necesita_movil: false,
        classification: duplicate?.classification || VOTER_CLASSIFICATIONS.WHITE,
        registration_origin: found ? 'ELECTORAL_REGISTER' : 'MANUAL',
        electoral_verification_status: found ? 'VERIFIED' : 'NOT_FOUND',
      });
      setStep('details');
    } catch (error) {
      setLookupError(error.message);
    } finally {
      setLookupLoading(false);
    }
  };

  const selectInscription = (inscriptionId) => {
    const official = padronResults.find(
      (item) => String(item.id) === String(inscriptionId)
    );
    if (!official) return;
    setSelectedInscription(official);
    setValue('inscripcion_id', official.id);
    setValue('cedula', String(official.cedula));
    setValue('nombres', official.nombre || '');
    setValue('apellidos', official.apellido || '');
    setValue('ciudad', official.distrito || '');
    setValue('local_votacion', official.local || '');
    setValue('mesa', official.mesa || '');
  };

  const submitRegistration = async (values) => {
    setSaving(true);
    setSaveError('');
    try {
      const response = registerFound
        ? await voterService.createFromPadron({
          inscripcion_id: values.inscripcion_id,
          puntero_id: values.puntero_id || undefined,
          telefono: values.telefono || existingVoter?.telefono,
          address: values.direccion || '',
          neighborhood: values.barrio || '',
          reference: values.referencia || '',
          classification: values.classification,
          needs_vehicle: Boolean(values.necesita_movil),
        })
        : await voterService.createForActor({
          ...values,
          mesa: values.mesa ? Number(values.mesa) : '',
          necesita_movil: Boolean(values.necesita_movil),
        }, user);
      onSuccess(response.data, { repeatedAssociation: response.data.repeated_document });
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const requiresCoordinatorSelection = [ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL].includes(user.role);
  const requiresLeaderSelection = user.role !== ROLES.PUNTERO;

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="lg"
      panelClassName={step === 'details'
        ? 'h-[calc(100svh-1.5rem)] min-h-[calc(100svh-1.5rem)] max-h-[calc(100svh-1.5rem)] sm:h-[min(52rem,calc(100svh-2rem))] sm:min-h-[min(52rem,calc(100svh-2rem))] sm:max-h-[min(52rem,calc(100svh-2rem))]'
        : undefined}
      title="Registrar votante"
      description={step === 'lookup' ? 'Primero consulta la cédula en el padrón electoral.' : 'Completa la información de contacto y gestión.'}
      footer={
        step === 'lookup' ? (
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={() => setStep('lookup')} disabled={saving}>
              <ArrowLeft className="size-4" /> Volver
            </Button>
            <Button type="submit" form="voter-registration-form" loading={saving}>
              {existingVoter ? 'Registrar con este puntero' : 'Guardar votante'}
            </Button>
          </>
        )
      }
    >
      {step === 'lookup' ? (
        <form onSubmit={searchRegister} className="py-3">
          <div className="mx-auto max-w-md text-center">
            <span className="mx-auto grid size-12 place-items-center rounded-full bg-primary-50 text-primary-700">
              <UserRoundSearch className="size-6" />
            </span>
            <h3 className="mt-4 font-semibold text-content-primary">Buscar en el padrón</h3>
            <p className="mt-1 text-sm text-content-muted">Ingresa el número sin puntos ni guiones.</p>
            <div className="mt-6 text-left">
              <Input
                id="registration-document-search"
                label="Número de cédula"
                value={document}
                onChange={(event) => setDocument(event.target.value.replace(/\D/g, ''))}
                placeholder="Ej. 4582921"
                inputMode="numeric"
                autoFocus
              />
            </div>
            {lookupError && (
              <Alert variant="danger" title="No se puede continuar" className="mt-4 text-left">
                {lookupError}
              </Alert>
            )}
            <Button type="submit" size="lg" className="mt-5 w-full" loading={lookupLoading}>
              <Search className="size-4" /> Buscar en padrón
            </Button>
          </div>
        </form>
      ) : (
        <>
          {existingVoter ? (
            <Alert variant="warning" title="La cédula ya está registrada" className="mb-5">
              {existingVoter.nombreCompleto} ya figura con otro responsable. Puedes registrarlo
              también con el puntero seleccionado; el dashboard advertirá la cédula repetida.
            </Alert>
          ) : registerFound ? (
            <Alert variant="success" title="Cédula encontrada en el padrón" className="mb-5">
              Los datos oficiales fueron completados y no pueden modificarse.
            </Alert>
          ) : (
            <Alert variant="warning" title="La cédula no figura en el padrón" className="mb-5">
              Puedes continuar con una carga manual. El registro quedará pendiente de verificación.
            </Alert>
          )}
          {saveError && <Alert variant="danger" title="No se pudo guardar" className="mb-5">{saveError}</Alert>}

          <form id="voter-registration-form" onSubmit={handleSubmit(submitRegistration)} className="space-y-6" noValidate>
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-content-primary">Datos de identificación</h3>
                <Badge variant={registerFound ? 'success' : 'warning'} dot>
                  {registerFound ? 'PADRON' : 'MANUAL'}
                </Badge>
              </div>
              {padronResults.length > 1 && (
                <Select
                  id="registration-padron-entry"
                  label="Inscripción encontrada"
                  value={selectedInscription?.id || ''}
                  onChange={(event) => selectInscription(event.target.value)}
                  options={padronResults.map((item) => ({
                    value: item.id,
                    label: `${item.nombre_apellido || `${item.nombre || ''} ${item.apellido || ''}`} · ${item.local || 'Sin local'} · Mesa ${item.mesa || '—'}`,
                  }))}
                  hint="La cédula tiene más de una inscripción. Selecciona la correcta."
                  required
                />
              )}
              <input type="hidden" {...register('inscripcion_id')} />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input id="registration-document" label="Cédula" readOnly required {...register('cedula')} />
                <Input id="registration-verification" label="Verificación" value={registerFound ? 'Verificado' : 'Pendiente'} readOnly />
                <Input id="registration-first-name" label="Nombres" readOnly={registerFound || Boolean(existingVoter)} required error={errors.nombres?.message} {...register('nombres', { required: 'Los nombres son obligatorios.' })} />
                <Input id="registration-last-name" label="Apellidos" readOnly={registerFound || Boolean(existingVoter)} required error={errors.apellidos?.message} {...register('apellidos', { required: 'Los apellidos son obligatorios.' })} />
                <Input id="registration-city" label="Ciudad/Distrito" readOnly={registerFound} required error={errors.ciudad?.message} {...register('ciudad', { required: 'La ciudad es obligatoria.' })} />
                <Input id="registration-polling-place" label="Local de votación" readOnly={registerFound} {...register('local_votacion')} />
                <Input id="registration-table" type="number" label="Mesa" readOnly={registerFound} {...register('mesa')} />
              </div>
              {registerFound && selectedInscription && (
                <div className="mt-4 grid gap-3 rounded-control border bg-slate-50 p-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                  <div><span className="text-content-muted">Nacionalidad</span><p className="font-medium">{selectedInscription.nacionalidad || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Fecha de nacimiento</span><p className="font-medium">{selectedInscription.fecha_nacimiento || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Sexo</span><p className="font-medium">{selectedInscription.sexo || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Departamento</span><p className="font-medium">{selectedInscription.departamento_codigo ?? '—'} · {selectedInscription.departamento || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Zona</span><p className="font-medium">{selectedInscription.zona_codigo ?? '—'} · {selectedInscription.zona || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Orden</span><p className="font-medium">{selectedInscription.orden ?? 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Tipo de mesa</span><p className="font-medium">{selectedInscription.tipo_mesa || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Pueblo indígena</span><p className="font-medium">{selectedInscription.pueblo_indigena || 'Sin datos'}</p></div>
                  <div><span className="text-content-muted">Comunidad indígena</span><p className="font-medium">{selectedInscription.comunidad_indigena || 'Sin datos'}</p></div>
                </div>
              )}
            </section>

            <section className="border-t pt-5">
              <h3 className="mb-3 text-sm font-semibold text-content-primary">
                {existingVoter ? 'Nuevo responsable solicitante' : 'Contacto y gestión'}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {!existingVoter && (
                  <>
                    <Input id="registration-phone" label="Teléfono" required error={errors.telefono?.message} {...register('telefono', { required: 'El teléfono es obligatorio.' })} />
                    <Input id="registration-address" label="Dirección" {...register('direccion')} />
                    <Input id="registration-neighborhood" label="Barrio" {...register('barrio')} />
                    <Input id="registration-reference" label="Referencia" {...register('referencia')} />
                  </>
                )}

                {requiresCoordinatorSelection && (
                  <Select
                    id="registration-coordinator"
                    label="Coordinador"
                    value={selectedCoordinatorId}
                    onChange={(event) => {
                      setSelectedCoordinatorId(event.target.value);
                      setValue('puntero_id', '');
                    }}
                    placeholder="Seleccionar coordinador"
                    options={options.coordinators.map((item) => ({ value: item.id, label: item.name }))}
                    required
                  />
                )}

                {requiresLeaderSelection ? (
                  <Select
                    id="registration-leader"
                    label="Puntero"
                    placeholder={requiresCoordinatorSelection && !selectedCoordinatorId ? 'Selecciona primero un coordinador' : 'Seleccionar puntero'}
                    disabled={requiresCoordinatorSelection && !selectedCoordinatorId}
                    options={availableLeaders.map((item) => ({ value: item.id, label: item.name }))}
                    required
                    error={errors.puntero_id?.message}
                    {...register('puntero_id', { required: 'Debes seleccionar un puntero.' })}
                  />
                ) : (
                  <Input id="registration-assigned-leader" label="Puntero" value={user.name} disabled readOnly hint="Asignado automáticamente desde tu sesión." />
                )}

                {!existingVoter && <div className="sm:col-span-2">
                  <Select id="registration-classification" label="Clasificación" options={classificationOptions} {...register('classification')} />
                  <div className="mt-3">
                    <ClassificationStatus
                      classification={selectedClassification}
                      generatesExpense={selectedClassification === VOTER_CLASSIFICATIONS.BLACK}
                    />
                  </div>
                </div>}
                {!existingVoter && <div className="flex min-h-16 items-end pb-1 sm:col-span-2">
                  <Checkbox id="registration-mobile" label="Necesita móvil" description="Incluir en la planificación de traslados." {...register('necesita_movil')} />
                </div>}
              </div>
            </section>

            <div className="rounded-control border border-info-100 bg-info-50 p-3 text-xs leading-5 text-info-700">
              {registerFound ? <CheckCircle2 className="mr-1.5 inline size-4" /> : <ShieldAlert className="mr-1.5 inline size-4" />}
              El coordinador y administrador general se obtendrán desde el puntero seleccionado. Cualquier valor manual será descartado.
            </div>
          </form>
        </>
      )}
    </Modal>
  );
}

export default VoterRegistrationFlow;
